// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Button, IconButton, ListItem, Typography } from '@mui/material';
import { AppIcon } from '@shared/ui-kit/components';
import { Translation } from '@shared/ui-kit/providers/translation-provider/translation';
import { classNames } from '@shared/ui-kit/utils/classnames';
import { defineComponent } from '@shared/ui-kit/utils/define-component';
import styles from './drawer-header.module.css';
import { useBankUploadDrawerHeader } from './use-drawer-header';

export const BankUploadDrawerHeader = defineComponent({
  displayName: 'BankUploadDrawerHeader',
  useSetup: useBankUploadDrawerHeader,
  View: ({
    discardDraft,
    onClose,
    showDiscard,
    showClose,
    headerMode,
    bankName,
    logoUrl,
    showLogoPreview,
    t,
  }) => (
    <ListItem
      component="div"
      className={classNames(
        styles.section,
        styles['section-sticky-top'],
        styles['section-header'],
      )}
      disablePadding
      disableGutters
    >
      <header
        className={styles['drawer-header']}
        data-testid="bank-upload-drawer-header"
      >
        {headerMode === 'bank' ? (
          <div
            className={styles['drawer-bank-identity']}
            data-testid="bank-upload-drawer-bank-identity"
          >
            <div className={styles['drawer-bank-logo']}>
              {showLogoPreview ? (
                <img
                  src={logoUrl}
                  alt=""
                  className={styles['drawer-bank-logo-image']}
                />
              ) : (
                <AppIcon icon="Landmark" size="lg" color="secondary" aria-hidden />
              )}
            </div>
            <Typography
              variant="h6"
              component="h2"
              className={styles['drawer-title']}
            >
              {bankName}
            </Typography>
          </div>
        ) : (
          <div className={styles['drawer-header-content']}>
            <Typography
              variant="h6"
              component="h2"
              className={styles['drawer-title']}
            >
              {headerMode === 'connect' ? (
                <Translation i18nKey="aggregator.select-bank" ns="aggregator" />
              ) : (
                <Translation
                  i18nKey="aggregator.create-bank.drawer.title"
                  ns="aggregator"
                />
              )}
            </Typography>
            {headerMode === 'connect' ? (
              <Typography variant="body2" className={styles['drawer-subtitle']}>
                <Translation
                  i18nKey="aggregator.search-bank-to-connect"
                  ns="aggregator"
                />
              </Typography>
            ) : null}
          </div>
        )}

        {showDiscard ? (
          <div className={styles['header-actions']}>
            <Button
              type="button"
              variant="text"
              color="error"
              className={styles['header-discard']}
              startIcon={<AppIcon icon="Trashcan" size="lg" color="inherit" />}
              onClick={discardDraft}
              data-testid="bank-upload-discard-draft"
            >
              <Translation
                i18nKey="aggregator.create-bank.discard.button"
                ns="aggregator"
              />
            </Button>
          </div>
        ) : null}

        {showClose ? (
          <div className={styles['header-actions']}>
            <IconButton
              type="button"
              size="small"
              className={styles['header-icon-action']}
              onClick={onClose}
              aria-label={t('common.close', { ns: 'common' })}
              data-testid="bank-upload-drawer-close"
            >
              <AppIcon icon="Close" color="inherit" />
            </IconButton>
          </div>
        ) : null}
      </header>
    </ListItem>
  ),
});
